'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { useTranslations } from 'use-intl';

import { resolveMatrixNaLabel } from '@/shared-project';
import { FORMAT_TRANSCRIPT_ANSWER, QuestionType } from '@/survey-graphql';
import type { GetProjectQuery } from '@/survey-graphql';
import { toast } from '@/ui';

import {
  isMatrixAnswer,
  NA_SENTINEL,
  type MatrixAnswer,
} from './survey-ai-forms-matrix.types';
import {
  getSortedSubQuestions,
  isMatrixQuestion,
} from './survey-ai-forms.helpers';
import type { FormAnswerValue } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface UseVoiceHandlingProps {
  setValue: (
    questionId: string,
    value: FormAnswerValue,
    options?: {
      shouldDirty?: boolean;
      shouldTouch?: boolean;
      shouldValidate?: boolean;
    }
  ) => void;
  onFormattingSettled?: () => void;
}

/**
 * Reusable hook for voice handling logic that can be used in both AI and Form sections
 * Handles transcript formatting and form value updates
 * Uses global currentTranscript from context (like chat does)
 */
export function useVoiceHandling({
  setValue,
  onFormattingSettled,
}: UseVoiceHandlingProps) {
  const t = useTranslations('AIForms');
  const onFormattingSettledRef = useRef(onFormattingSettled);
  useEffect(() => {
    onFormattingSettledRef.current = onFormattingSettled;
  }, [onFormattingSettled]);

  // GraphQL query for formatting transcript answers
  const [formatTranscriptAnswer, { loading: isFormattingTranscript }] =
    useLazyQuery(FORMAT_TRANSCRIPT_ANSWER, {
      errorPolicy: 'all',
    });

  // Function to format transcript answer using the API
  const formatTranscriptAnswerHandler = useCallback(
    async (question: ProjectQuestion, transcript: string) => {
      if (!transcript.trim()) return [];

      try {
        const { data, error } = await formatTranscriptAnswer({
          variables: {
            input: {
              questionType: question.type,
              options: question.options?.map((opt) => ({
                value: opt.value,
                label: opt.label,
              })),
              notApplicableLabel:
                resolveMatrixNaLabel(
                  question.notApplicableLabel,
                  t('matrix.notApplicableDefault')
                ) ?? undefined,
              transcript: transcript,
            },
          },
        });

        // Handle GraphQL errors
        if (
          error ||
          !data?.formattedTranscriptAnswer?.extractedOptions ||
          data?.formattedTranscriptAnswer?.extractedOptions?.length === 0
        ) {
          toast.add({
            title: t('toast.selectOption'),
            type: 'error',
          });
          return null;
        }

        const { extractedOptions, formattedTranscript } =
          data.formattedTranscriptAnswer;

        if (extractedOptions && extractedOptions.length > 0) {
          if (question.type !== QuestionType.OpenEnded) {
            // For rating and single select questions, return the first value as a string
            if (
              question.type === QuestionType.Rating ||
              (question.type === QuestionType.MultipleChoice &&
                (question.maxSelections ?? 1) === 1)
            ) {
              return extractedOptions[0] || '';
            }
            // For multi-select and ranking questions, return the array
            return extractedOptions;
          }
        }

        return formattedTranscript;
      } catch {
        // Show user-friendly error message using toast
        toast.add({
          title: t('toast.voiceFailed'),
          type: 'error',
        });
        return null;
      } finally {
        onFormattingSettledRef.current?.();
      }
    },
    [formatTranscriptAnswer, t]
  );

  const processMatrixTranscript = useCallback(
    async (
      question: ProjectQuestion,
      transcript: string,
      currentValue?: FormAnswerValue
    ): Promise<MatrixAnswer | null> => {
      if (!transcript.trim()) return null;
      const sortedSubQuestions = getSortedSubQuestions(question);
      if (sortedSubQuestions.length === 0) return null;

      try {
        const { data, error } = await formatTranscriptAnswer({
          variables: {
            input: {
              questionType: question.type,
              options: question.options?.map((opt) => ({
                value: opt.value,
                label: opt.label,
              })),
              notApplicableLabel:
                resolveMatrixNaLabel(
                  question.notApplicableLabel,
                  t('matrix.notApplicableDefault')
                ) ?? undefined,
              subQuestions: sortedSubQuestions.map((sq) => ({
                id: sq.id,
                text: sq.text ?? '',
              })),
              transcript,
            },
          },
        });

        const extractedSubQuestionAnswers =
          data?.formattedTranscriptAnswer?.extractedSubQuestionAnswers;

        if (error || !extractedSubQuestionAnswers?.length) {
          toast.add({
            title: t('toast.selectRatings'),
            type: 'error',
          });
          return null;
        }

        const subQuestionIds = new Set(sortedSubQuestions.map((sq) => sq.id));
        const baseMatrix: MatrixAnswer = isMatrixAnswer(currentValue)
          ? { ...currentValue }
          : {};
        extractedSubQuestionAnswers.forEach(({ subQuestionId, value }) => {
          if (!subQuestionIds.has(subQuestionId) || !value) return;
          baseMatrix[subQuestionId] =
            value === NA_SENTINEL ? NA_SENTINEL : value;
        });

        return Object.keys(baseMatrix).length > 0 ? baseMatrix : null;
      } catch {
        toast.add({
          title: t('toast.voiceFailed'),
          type: 'error',
        });
        return null;
      }
    },
    [formatTranscriptAnswer, t]
  );

  const processTranscript = useCallback(
    async (
      question: ProjectQuestion,
      transcript: string,
      currentValue?: FormAnswerValue
    ): Promise<FormAnswerValue | null> => {
      if (!transcript.trim()) return null;

      if (isMatrixQuestion(question)) {
        const matrixAnswer = await processMatrixTranscript(
          question,
          transcript,
          currentValue
        );
        if (matrixAnswer) {
          setValue(question.id, matrixAnswer, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          return matrixAnswer;
        }
        return null;
      }

      if (question.type === QuestionType.OpenEnded) {
        // For open-ended: use transcript directly
        setValue(question.id, transcript, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        // Note: Transcript should be saved by caller before calling processTranscript
        return transcript;
      } else {
        // For other question types: format transcript and update the question
        const formattedAnswer = await formatTranscriptAnswerHandler(
          question,
          transcript
        );

        if (formattedAnswer) {
          // Use the formatted answer directly (it's already in the correct format)
          setValue(question.id, formattedAnswer, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          // Note: Transcript should be saved by caller before calling processTranscript
          return formattedAnswer;
        }
      }
      return null;
    },
    [setValue, formatTranscriptAnswerHandler, processMatrixTranscript]
  );

  return {
    isFormattingTranscript,
    processTranscript,
    processMatrixTranscript,
    formatTranscriptAnswerHandler,
  };
}
